import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { selectAccessLevel } from '../../../slices/subscriptions/subscriptionsSlice';
import { useNavigate } from 'react-router-dom';
import { Check } from 'react-feather';
import { handleCheckout } from '../../../services/subscriptions';
import LightStreak from '../../../assets/images/light-streak.png';
import {
  SubscriptionPlan,
  SubscriptionAccess,
} from '../../../types/Subscription';
import { examsSelectors } from '../../../slices/exams/examsSlice';
import posthog from 'posthog-js';
import OraGuarantee from '../../../assets/svgs/og.svg';

interface PricingOptionProps {
  isAuthenticated: boolean;
  title: string;
  description: string;
  monthlyPrice: string;
  annualPrice: string;
  features: string[];
  savings?: string;
  savingPercent?: string;
  isGroupOption?: boolean;
  product: SubscriptionAccess;
  plan: SubscriptionPlan;
  guarantee: boolean;
}

const PricingOption: React.FC<PricingOptionProps> = ({
  isAuthenticated,
  title,
  description,
  monthlyPrice,
  annualPrice,
  features,
  savings,
  savingPercent,
  isGroupOption,
  product,
  plan,
  guarantee,
}) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const accessLevel = useSelector(selectAccessLevel);
  const primaryExamAccessLevel = useSelector(
    examsSelectors.selectPrimaryExamAccessLevel
  );

  const navigate = useNavigate();

  const renderFeatures = (features: string[], guarantee: boolean) => {
    return (
      <div className="pricing-option_inner_bottom_features">
        <h3>
          <span>Features</span>
          <span className="line"></span>
        </h3>
        <ul>
          {guarantee && (
            <li>
              <Check />
              <button
                onClick={() => {
                  const guaranteeElement = document.getElementById('guarantee');
                  if (guaranteeElement) {
                    guaranteeElement.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                <img
                  className="ora-guarantee"
                  src={OraGuarantee}
                  alt="Ora Guarantee"
                />
                <span>Ora Guarantee</span>
              </button>
            </li>
          )}
          {features.map((feature) => (
            <li>
              <Check />
              {feature}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const renderCheckoutButton = () => {
    if (isGroupOption) {
      return 'Create Group';
    }
    const buttonText = () => {
      if (isAuthenticated) {
        if (accessLevel === SubscriptionAccess.FREE) {
          if (monthlyPrice === '0') {
            return 'Current Plan';
          } else {
            return 'Subscribe Now';
          }
        } else {
          if (monthlyPrice === '0') {
            return 'Current Plan';
          } else {
            return 'Get Started';
          }
        }
      } else {
        if (monthlyPrice === '0') {
          return 'Free Forever';
        } else {
          return 'Get Started';
        }
      }
    };

    return (
      <button
        className="button button--get-started"
        onClick={() => {
          if (isGroupOption) {
            navigate('/group');
          } else if (isAuthenticated) {
            posthog.capture('pricing_option_clicked', {
              product,
              plan,
            });
            handleCheckout(product, plan);
          } else {
            const nonAuthCheckoutLink = `/auth?signup=true&product=${product}&plan=${plan}`;
            navigate(nonAuthCheckoutLink);
          }
        }}
        disabled={monthlyPrice === '0'}
      >
        {buttonText()}
      </button>
    );
  };

  return (
    <div
      className={`pricing-option pricing-option${currentUser ? ' pricing-option--authed' : ''}${
        product === primaryExamAccessLevel ? ' pricing-option--primary' : ''
      }`}
    >
      <div className="pricing-option_inner">
        {savingPercent && plan === 'ANNUAL' && (
          <div className="savings-pill">Save {savingPercent}</div>
        )}
        <div className="pricing-option_inner_top">
          <h2>{title}</h2>
          {description && <p>{description}</p>}
          <div className="pricing-option_inner_top_price">
            <div className="pricing-option_inner_top_price_amount">
              <span className="dollar-sign">$</span>
              {plan === 'ANNUAL' ? annualPrice : monthlyPrice}
            </div>
            <div className="pricing-option_inner_top_price_label">
              per month
            </div>
            {plan === 'ANNUAL' && (
              <div className="savings">{savings && <p>{savings}</p>}</div>
            )}
          </div>
          {renderCheckoutButton()}
        </div>
        <div className="pricing-option_inner_bottom">
          {renderFeatures(features, guarantee)}
        </div>
        {product === primaryExamAccessLevel && (
          <div className="gradient-blur"></div>
        )}
      </div>
      {product === primaryExamAccessLevel && (
        <>
          <img className="light-streak" src={LightStreak} alt="" />
          <img
            className="light-streak light-streak--2"
            src={LightStreak}
            alt=""
          />
          <img
            className="light-streak light-streak--3"
            src={LightStreak}
            alt=""
          />
        </>
      )}
    </div>
  );
};

export default PricingOption;
